/**
 * rsa-intro-split block
 *
 * Two-column intro section below the hero:
 * - illustration on one side (left by default; set image_position: right)
 * - eyebrow label (one line per array entry), heading (supports <br/>),
 *   body copy, optional bold callout on the other.
 */
import { Button } from "~/components/Button";

export function RsaIntroSplit({
  image,
  image_alt,
  image_position = "left",
  image_size = "md",
  image_rounded = "2xl",
  vertical_align = "center",
  eyebrow_tracking = "wide",
  eyebrow,
  heading,
  heading_size = "default",
  copy,
  callout,
  callout_note,
  button_primary,
}: {
  image?: string;
  image_alt?: string;
  /** Which side the illustration sits on. Defaults to "left". */
  image_position?: "left" | "right";
  /** Relative width of the illustration column. "md" (default) = 5/12; "lg" = 6/12; "xl" = 7/12. */
  image_size?: "md" | "lg" | "xl";
  /** Corner radius of the illustration. Defaults to "2xl". */
  image_rounded?: "lg" | "xl" | "2xl";
  /** Vertical alignment of the two columns. "center" (default) or "top". */
  vertical_align?: "center" | "top";
  /** Letter-spacing of the eyebrow label. "wide" (default) or "normal". */
  eyebrow_tracking?: "wide" | "normal";
  eyebrow?: string | string[];
  heading?: string;
  /** Heading size. "default" (31px / lg 36px) or "28.4" (fixed 28.4px). */
  heading_size?: "default" | "28.4";
  copy?: string;
  callout?: string;
  /** Short emphasized line rendered below the callout (e.g. "Booth 916"). */
  callout_note?: string;
  button_primary?: { text: string; url: string; target?: string; rel?: string };
}) {
  const eyebrowLines = Array.isArray(eyebrow) ? eyebrow : eyebrow ? [eyebrow] : [];

  // Static class maps so Tailwind's JIT can see every variant.
  const imageCol =
    image_size === "xl" ? "lg:col-span-7" : image_size === "lg" ? "lg:col-span-6" : "lg:col-span-5";
  const textCol = !image
    ? "lg:col-span-12"
    : image_size === "xl"
      ? "lg:col-span-5"
      : image_size === "lg"
        ? "lg:col-span-6"
        : "lg:col-span-7";
  const roundedClass =
    image_rounded === "lg" ? "rounded-lg" : image_rounded === "xl" ? "rounded-xl" : "rounded-2xl";
  const alignClass = vertical_align === "top" ? "items-start" : "items-center";
  const eyebrowTracking = eyebrow_tracking === "normal" ? "tracking-normal" : "tracking-widest-2";
  const headingSize = heading_size === "28.4" ? "text-[28.4px]" : "text-31 lg:text-4xl";

  const imageBlock = image && (
    <div className={imageCol}>
      <div className={`${roundedClass} overflow-hidden bg-gray-100/40`}>
        <img src={image} alt={image_alt || ""} className="w-full" loading="lazy" />
      </div>
    </div>
  );

  const textBlock = (
    <div className={textCol}>
      {eyebrowLines.length > 0 && (
        <div className="mb-5 space-y-0.5">
          {eyebrowLines.map((line, i) => (
            <p
              key={i}
              className={`font-body text-teal-dark font-semibold text-sm md:text-base uppercase ${eyebrowTracking}`}
            >
              {line}
            </p>
          ))}
        </div>
      )}
      {heading && (
        <h2
          className={`font-display text-charcoal font-bold ${headingSize} leading-tight max-w-2xl`}
          dangerouslySetInnerHTML={{ __html: heading }}
        />
      )}
      {copy && (
        <p className="font-body text-gray-80 text-lg mt-5 leading-relaxed max-w-2xl whitespace-pre-line">
          {copy}
        </p>
      )}
      {callout && (
        <p className="font-body text-charcoal font-semibold text-lg mt-5 leading-relaxed max-w-2xl whitespace-pre-line">
          {callout}
        </p>
      )}
      {callout_note && (
        <p className="font-body text-teal-dark font-semibold text-lg mt-2 leading-relaxed max-w-2xl whitespace-pre-line">
          {callout_note}
        </p>
      )}
      {button_primary && (
        <div className="mt-6">
          <Button
            href={button_primary.url}
            variant="teal"
            target={button_primary.target}
            rel={button_primary.rel}
          >
            <span>{button_primary.text}</span>
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="px-4 md:px-8 py-16 lg:py-20">
      <div className="max-w-screen-xl mx-auto">
        <div className={`grid gap-8 lg:gap-16 lg:grid-cols-12 ${alignClass}`}>
          {image_position === "right" ? (
            <>
              {textBlock}
              {imageBlock}
            </>
          ) : (
            <>
              {imageBlock}
              {textBlock}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
